import * as Sentry from '@sentry/react';

import { BaseLogger, backoffWithJitter } from '@anycable/core';
import { createCable } from '@anycable/web';

import { abortSignalAfter } from './lib/abortSignalAfter';
import { apiGet } from './lib/api_v2';
import { cableTokenNeedsRefresh, keepCableConnected } from './lib/cableLiveness';
import logger from './lib/logger';
import { getMetaContent, isFeatureFlagEnabled } from './lib/meta';

const PING_INTERVAL_MS = 3000;
// Without a cap the default strategy doubles forever; ten failed attempts put the next try an hour out.
const RECONNECT_MAX_INTERVAL_MS = 30_000;
// A refresh that hangs (half-open socket after wake) would pin the cable in `connecting`, where
// neither the monitor nor keepCableConnected will touch it. A deadline turns it into a failed
// attempt that the reconnect backoff retries.
const TOKEN_REFRESH_TIMEOUT_MS = 15_000;

const SENTRY_LEVELS = { warn: 'warning', error: 'error', fatal: 'error' };

class CableLogger extends BaseLogger {
  writeLogEntry(level, msg, details) {
    if (details && (details.type === 'ping' || details.command === 'pong')) return;
    logger[level]('cable', msg, details);
    // Only fatal app logs reach Sentry, so transport trouble was invisible in production.
    if (SENTRY_LEVELS[level]) {
      Sentry.addBreadcrumb({ category: 'cable', level: SENTRY_LEVELS[level], message: msg, data: details });
    }
  }
}

async function refreshCableUrl(transport) {
  const signal = abortSignalAfter(TOKEN_REFRESH_TIMEOUT_MS);
  const response = await apiGet('/cable', signal ? { signal } : {});
  const data = await response.json().catch(() => undefined);
  const url = data?.data?.cable?.url;
  if (!response.ok || !data?.ok || typeof url !== 'string') {
    throw new Error(`cable token refresh failed (${response.status})`);
  }
  transport.setURL(url);
  logger.info('cable', 'Set refreshed cable URL', { url });
  Sentry.addBreadcrumb({ category: 'cable', level: 'info', message: 'token refreshed before connect' });
}

const reconnectDelay = backoffWithJitter(PING_INTERVAL_MS, { maxInterval: RECONNECT_MAX_INTERVAL_MS });

const cable = createCable({
  logger: new CableLogger(isFeatureFlagEnabled('debug_cable') ? 'debug' : 'info'),
  protocol: 'actioncable-v1-ext-json',
  lazy: !getMetaContent('oauth2:client_id'), // don't connect on signed out users
  protocolOptions: { pongs: true, disableSessionRecovery: true },
  pingInterval: PING_INTERVAL_MS,
  reconnectStrategy: reconnectDelay,
  // Runs before every transport open. The cable JWT lives for an hour and the
  // server rejects an expired one with a terminal close, so any token inside its
  // refresh margin is swapped here, ahead of the handshake. A refresh failure
  // fails only this attempt and hands control back to the reconnect backoff.
  transportConfigurator: async (transport) => {
    if (!cableTokenNeedsRefresh(transport.url, Date.now())) return;
    await refreshCableUrl(transport);
  },
});

cable.on('connect', (event) => {
  const detail = { connected: true, reconnect: event.reconnect };
  window.dispatchEvent(new CustomEvent('output:internal:cable', { detail }));
  logger.debug('cable', 'output:internal:cable:connect', detail);
});

cable.on('disconnect', (event) => {
  const detail = { connected: false, reason: event.reason };
  window.dispatchEvent(new CustomEvent('output:internal:cable', { detail }));
  logger.debug('cable', 'output:internal:cable:disconnect', detail);
});

cable.on('close', (event) => {
  const detail = { connected: false, reason: event ? JSON.stringify(event) : 'unspecified' };
  window.dispatchEvent(new CustomEvent('output:internal:cable', { detail }));
  logger.debug('cable', 'output:internal:cable:close', detail);
  // A server close used to be terminal for the page. The configurator should make token_expired
  // closes vanish, so each one that still lands is worth a Sentry event.
  if (event) {
    Sentry.captureMessage('Cable closed by server; reopening with backoff', {
      level: 'warning',
      extra: { reason: event.reason ?? 'unspecified' },
      fingerprint: ['cable-server-close', String(event.reason ?? 'unspecified')],
    });
  }
});

keepCableConnected({
  cable,
  reconnectDelay,
  eventTarget: window,
  lifecycleDocument: document,
  resumeSource: window.electron?.system,
  log: (message, details) => logger.info('cable', message, details),
});

export default cable;
